<template>
  <div :class="['font-background-selector', { 'font-background-selector--disabled': disabled }]">
    <div
      v-for="(backgroundColor, index) in backgroundColors"
      :key="index"
      :class="[
        'font-background-selector__item',
        {
          'font-background-selector__item--selected': backgroundColor.color === selectedFontBackgroundColor
        }
      ]"
      @click="emits('fontBackgroundColorSelected', backgroundColor.color)"
    >
      <div :class="['font-background-selector__item-contents', `font-background-selector__item-contents--${backgroundColor.name}`]">
        Aa
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { CssColours } from '@/static/colours'

/**
 * Subset of CSS properties - just two properties that are required
 */
interface backgroundColorOption {
  name: string
  color: string
}

/**
 * Font background available styles
 */
const backgroundColors: backgroundColorOption[] = [
  {
    name: 'transparent',
    color: CssColours.TRANSPARENT
  },
  {
    name: 'black',
    color: CssColours.BLACK
  },
  {
    name: 'white',
    color: CssColours.WHITE
  }
]

const props = defineProps<{
  selectedFontBackgroundColor: string | null,
  disabled: boolean
}>()

const emits = defineEmits<{
  (event: 'fontBackgroundColorSelected', backgroundColor: string): void
}>()

</script>
<style lang="scss">
.font-background-selector {
  $root: #{&};
  display: flex;
  flex-direction: row;
  gap: 6px;

  // Weird implementation alert! The following element is implemented as the following:
  // - root (__item)
  //    This sets up the size of the element, border radius and background colour (this is not visible by default)
  // - ::before
  //    This is the checkered background. It is implemented as a pseudo-element as it needs to 'blend' with the
  //    background on hover, by reducing its opacity
  // - contents (__item-contents)
  //    This is the box which has the Aa example lettering inside it. It is styled both by classes and by JS
  // - ::after
  //    This is for the border when an item is selected. The reason why the border is implemented using this
  //    pseudo class is because if the parent has a border on selected, it moves the background by the border width
  //    and it looks jarring when toggled. This way it just applies the border on top, without changing any of the
  //    dimensions of the background etc.
  &__item {
    position: relative;
    width: 60px;
    height: 40px;
    border-radius: 2px;
    cursor: pointer;
    transition: background-color 150ms $animationCurve;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      // Checkerboard implementation taken from:
      // https://css-tricks.com/background-patterns-simplified-by-conic-gradients/#checkerboard
      $checkerboard-size: 14px;
      background: repeating-conic-gradient($black 0% 25%, $grey-80 0% 50%) 0/#{$checkerboard-size $checkerboard-size};
      opacity: 0.5;
      border-radius: 2px;
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      border-radius: 2px;
    }

    &:hover {
      background-color: $grey-80;
      &::before {
        // Opacity of the checkered background decreases on hover
        opacity: 0.4;
      }
    }

    &--selected {
      &::after {
        // Border is applied in '::after' pseudoselector rather than the element itself to prevent the background from
        // shifting when toggling between selection states
        border: 2px solid $white;
      }

      &:hover {
        // When selected, there should be no changes on hover
        background-color: transparent;
        &::before {
          opacity: 0.5;
        }
      }
    }

    &-contents {
      // Center absolutely
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      
      width: 44px;
      height: 24px;
      font-size: 18px;
      font-family: $font-bold;
      text-align: center;
      line-height: 22px;
      letter-spacing: 2px;

      &--transparent {
        color: $white;
        background-color: transparent;
      }

      &--black {
        color: $white;
        background-color: $black;
      }

      &--white {
        color: $black;
        background-color: $white;
      }
    }
  }

  &--disabled {
    pointer-events: none;

    #{$root}__item-contents {
      &--transparent {
        color: $grey-80;
      }

      &--black {
        color: $grey-80;
      }

      &--white {
        background-color: $grey-80;
      }
    }
  }
}
</style>